import { Icon } from "@blueprintjs/core"
import CustomButton from "components/Atoms/CustomButton"
import NewTag from "components/Atoms/NewTag"
import StyledLink from "components/Atoms/StyledLink"
import QueryBar from "components/Common/QueryBar"
import TablePagination from "components/Molecules/TablePagination"
import OverFlowMenu from "components/Organisms/OverFlowMenu"
import SubHeader from "components/Organisms/SubHeader"
import CustomReactTable from "components/Templates/CustomReactTable"
import { useAllRoutesQuery } from "generated/graphql"
import useSearchParams from "hooks/useSearchParams"
import fsmCreateRouteWalkthroughSteps from "pages/fsm/walkthrough/steps"
import React, { useCallback, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import isNew from "utils/is-new"
import useFSMWalkthrough, { Walkthrough } from "../walkthrough.store"

export const ROUTE_MASTER_PAGE_SIZE = 15
type RouteListPageType = {}
const RouteListPage = ({}: RouteListPageType) => {
  const navigate = useNavigate()

  const [query, setSearch] = useSearchParams()
  const setState = useFSMWalkthrough(useCallback(store => store.setState, []))
  const state = useFSMWalkthrough(store => store.state)
  const completed = state?.completed
  const isCompleted = completed?.[Walkthrough.CREATE_ROUTE] === "1"

  const startWalkthrough = useCallback(() => {
    setTimeout(() => {
      setState({
        run: true,
        stepIndex: 0,
        steps: fsmCreateRouteWalkthroughSteps,
        type: Walkthrough.CREATE_ROUTE,
      })
    }, 1000)
  }, [setState])

  useEffect(() => {
    if (!isCompleted) {
      startWalkthrough()
    }
  }, [isCompleted])
  const { data, loading } = useAllRoutesQuery({
    variables: {
      first: ROUTE_MASTER_PAGE_SIZE,
      skip: ROUTE_MASTER_PAGE_SIZE * query.page || 0,
      query: query.search,
    },
  })

  const COLUMNS = [
    {
      width: 50,
      Header: "Sr.no.",
      Cell({ index }) {
        return index + 1
      },
    },
    {
      width: 120,
      Header: "Status",
      accessor: "isActive",
      Cell({ value }) {
        return (
          <>
            <Icon icon="dot" intent={value ? "success" : "danger"} />
            {value ? "Active" : "Inactive"}
          </>
        )
      },
    },
    {
      width: 270,
      Header: "Route Name",
      accessor: "name",
      Cell({ original }) {
        return (
          <div className="flex items-center">
            <span
              className="w-3 h-3 rounded mr-2"
              style={{ background: original.colorCode }}
            />
            <div>
              <StyledLink to={`/d/fsm/routes/${original?.id}`}>
                {original.name}
              </StyledLink>
              <NewTag
                tolerance={1}
                createdAt={original?.createdAt}
                toleranceUnit="minutes"
              />
            </div>
          </div>
        )
      },
    },
    {
      width: 200,
      Header: "Survey Area",
      accessor: "surveyArea",
      Cell({ value }) {
        return value?.properties?.name
      },
    },
    {
      Header: "Outlets",
      accessor: "points",
      Cell({ value }) {
        if (!value?.length) return "---"
        return (
          <OverFlowMenu
            renderText={item => item.visitee?.fullName}
            intent="primary"
            icon="user"
            onClick={item =>
              navigate(`/d/registration/vendors/details/${item.visitee?.id}`)
            }
            items={value}
          />
        )
      },
    },
    {
      Header: "Total Outlets",
      width: 130,
      Cell({ original }) {
        return original.points?.length
      },
    },
  ]

  return (
    <SubHeader
      showBackButton={false}
      breadrumbs={[
        {
          href: "/d/fsm/routes",
          text: "Routes",
          onClick(e) {
            e.preventDefault()
            navigate("/d/fsm/routes")
          },
        },
      ]}
      header={
        <>
          <QueryBar
            round={false}
            value={query?.query}
            onChange={value => {
              setSearch({ ...query, search: value })
            }}
          />
          <div className="flex">
            <TablePagination
              className="mr-3"
              page={query.page}
              onPageChange={page => setSearch({ ...query, page })}
            />
            <div>
              <CustomButton
                className="mr-3"
                intent="success"
                onClick={startWalkthrough}
                outlined
                icon="help"
              />
              <CustomButton
                onClick={() => {
                  setState({
                    run: false,
                    stepIndex: state.stepIndex === 0 ? 1 : state.stepIndex,
                  })
                  navigate("new")
                  setState({ run: true })
                }}
                id="fsm-add-route"
                icon="plus"
                intent="success"
              >
                Route
              </CustomButton>
            </div>
          </div>
        </>
      }
    >
      <div className="shadow-sm rounded-lg bg-white overflow-hidden">
        <CustomReactTable
          getTrProps={(_, rowInfo) => {
            const newRow = isNew(rowInfo?.original?.createdAt, 1)
            return {
              className: newRow ? "bg-green-100 fsm-new-route" : "",
              id: newRow ? "fsm-new-route" : "",
            }
          }}
          pageSize={ROUTE_MASTER_PAGE_SIZE}
          loading={loading}
          loadingText="Fetching Routes..."
          className="rounded-lg"
          columns={COLUMNS}
          data={data?.allRoutes}
        />
      </div>
    </SubHeader>
  )
}

export default RouteListPage
